import type { Schedule } from '@/types/notification'

/** Query param that previews the Voice AI reminder channel on non-production builds. */
export const VOICE_AI_REMINDER_PREVIEW_PARAM = 'voiceAiReminders'

export const VOICE_AI_REMINDER_PREVIEW_STORAGE_KEY = 'ghl_voice_ai_reminders_preview'

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/**
 * The channel is gated on a location flag supplied by the host shell. Until that flag exists
 * everywhere, staging and local builds can preview it with `?voiceAiReminders=true`; the choice is
 * remembered per browser (`=false` forgets it) because the shell drops unknown query params on its
 * own navigations. Beta, interop and production builds ignore both so customers never see it early.
 */
export const resolveVoiceAiNotificationsEnabled = (
  hostFlag: unknown,
  queryParams: Record<string, unknown> | null | undefined,
  mode: string,
  storage?: StorageLike | null
): boolean => {
  if (hostFlag === true) return true
  if (mode !== 'staging') return false
  const requested = queryParams?.[VOICE_AI_REMINDER_PREVIEW_PARAM]
  try {
    if (requested === 'true') storage?.setItem(VOICE_AI_REMINDER_PREVIEW_STORAGE_KEY, 'true')
    else if (requested === 'false') storage?.removeItem(VOICE_AI_REMINDER_PREVIEW_STORAGE_KEY)
    return String(requested ?? '') === 'true' || storage?.getItem(VOICE_AI_REMINDER_PREVIEW_STORAGE_KEY) === 'true'
  } catch {
    return String(requested ?? '') === 'true'
  }
}

/**
 * Outbound calls are only placed between these hours in the contact's local time. A reminder
 * whose call time falls outside the window is deferred to the next opening — which, for an
 * early-morning appointment, can be after the appointment has started.
 */
export const VOICE_CALLING_WINDOW = { startHour: 8, endHour: 20 } as const

export type VoiceReminderUnit = 'minutes' | 'hours' | 'days'

type OffsetBounds = Partial<Record<VoiceReminderUnit, { min: number; max: number }>>

/**
 * Until reminder calls can be prioritised over other outbound traffic, a call is only promised a day
 * or two ahead: under load, a same-day reminder could be dialled after the appointment started.
 * Staging builds also offer minutes and hours so the flow can be tested end to end in minutes.
 */
const RELEASE_OFFSET_BOUNDS: OffsetBounds = { days: { min: 1, max: 2 } }
const STAGING_OFFSET_BOUNDS: OffsetBounds = {
  minutes: { min: 1, max: 120 },
  hours: { min: 1, max: 48 },
  days: { min: 1, max: 2 },
}

const offsetBoundsFor = (mode: string): OffsetBounds => (mode === 'staging' ? STAGING_OFFSET_BOUNDS : RELEASE_OFFSET_BOUNDS)

export const getVoiceReminderUnits = (mode: string): VoiceReminderUnit[] => Object.keys(offsetBoundsFor(mode)) as VoiceReminderUnit[]

export const DEFAULT_VOICE_REMINDER_OFFSET: Schedule = { timeOffset: 1, unit: 'days' }

/** The bounds an offset violates, or null when it is a usable call time on this build. */
export const getVoiceReminderOffsetError = (
  schedule: Schedule | undefined,
  mode: string
): { unit: VoiceReminderUnit; min: number; max: number } | null => {
  const bounds = offsetBoundsFor(mode)
  const unitBounds = schedule ? bounds[schedule.unit as VoiceReminderUnit] : undefined
  if (!schedule || !unitBounds) {
    return { unit: 'days', ...(bounds.days as { min: number; max: number }) }
  }
  const value = Number(schedule.timeOffset)
  if (!Number.isInteger(value) || value < unitBounds.min || value > unitBounds.max) {
    return { unit: schedule.unit as VoiceReminderUnit, ...unitBounds }
  }
  return null
}

const MINUTES_PER_DAY = 24 * 60

const UNIT_MINUTES: Record<string, number> = {
  minutes: 1,
  mins: 1,
  hours: 60,
  days: 60 * 24,
  weeks: 60 * 24 * 7,
  months: 60 * 24 * 30,
}

export const scheduleToMinutes = (schedule: Schedule): number => {
  const perUnit = UNIT_MINUTES[schedule.unit] ?? 0
  return Math.max(0, Math.trunc(Number(schedule.timeOffset) || 0)) * perUnit
}

const clockLabel = (minutesFromMidnight: number): string => {
  const normalised = ((minutesFromMidnight % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  const hour24 = Math.floor(normalised / 60)
  const minute = normalised % 60
  const suffix = hour24 < 12 ? 'am' : 'pm'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return minute === 0 ? `${hour12}${suffix}` : `${hour12}:${String(minute).padStart(2, '0')}${suffix}`
}

/**
 * The reminder fires at `appointmentStart - offset`, so the appointment start times whose call
 * still lands in the calling window are that window shifted forward by the offset.
 */
export const getCallingWindowAdvice = (schedule: Schedule | undefined): { safeFromLabel: string; safeUntilLabel: string } => {
  const offsetMinutes = schedule ? scheduleToMinutes(schedule) : 0
  return {
    safeFromLabel: clockLabel(VOICE_CALLING_WINDOW.startHour * 60 + offsetMinutes),
    safeUntilLabel: clockLabel(VOICE_CALLING_WINDOW.endHour * 60 + offsetMinutes),
  }
}

/** The host shell's origin (the calendar app runs in its iframe), for links that must open in the shell. */
export const getShellOrigin = (): string => {
  try {
    const ancestor = window.location.ancestorOrigins?.[0]
    if (ancestor) return ancestor
    if (document.referrer) return new URL(document.referrer).origin
  } catch {
    // fall through
  }
  return window.location.origin
}

export const getVoiceAgentUrl = (locationId: string, agentId: string): string =>
  `${getShellOrigin()}/v2/location/${locationId}/ai-agents/voice-ai/builder/${agentId}`
